import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CaretUpIcon } from '@stashinvest/react-assets';
import { logOutDev } from 'stash/actions/session';
import { DismissPayloadType } from 'stash/constants/bridgeMessageTypes';
import { dismissWebview } from 'stash/utils/dismissWebview';
import { isNativeWebview } from 'stash/utils';
import { EXPERIMENTS } from 'stash/components/experiments';
import { usePersistedState } from 'stash/hooks/usePersistedState';

const CaretUpIconWithStyle = (
	props: React.ComponentProps<typeof CaretUpIcon> & { style?: React.CSSProperties }
) => {
	return <CaretUpIcon {...props} />;
};

const DEVTOOLS_HIDDEN_KEY = 'stash.devTools.hidden';

type OverrideValue = string | boolean;
type OverridesMap = Record<string, OverrideValue>;

function readOverrideFromStorage(experimentKey: string): OverrideValue | undefined {
	const raw = localStorage.getItem(experimentKey);
	if (raw === null) return undefined;
	if (raw === 'true') return true;
	if (raw === 'false') return false;
	return raw;
}

function hydrateOverridesFromStorage(): OverridesMap {
	const initial: OverridesMap = {};
	for (const experimentKey of Object.values(EXPERIMENTS)) {
		const value = readOverrideFromStorage(experimentKey);
		if (value !== undefined) initial[experimentKey] = value;
	}
	return initial;
}

function DevTools() {
	const dispatch = useDispatch();
	const isLoggedIn = useSelector(
		(state: { entities: { api_key: { uuid?: string } } }) => !!state.entities.api_key.uuid
	);
	const [isOpen, setIsOpen] = useState(false);
	const [isHidden, setIsHidden] = usePersistedState(false, {
		key: DEVTOOLS_HIDDEN_KEY,
	});
	const [overrides, setOverrides] = useState<OverridesMap>(() => hydrateOverridesFromStorage());
	const [showOverrideModal, setShowOverrideModal] = useState(false);
	const [selectedExperiment, setSelectedExperiment] = useState<{
		key: string;
		experimentKey: string;
	} | null>(null);
	const [overrideValue, setOverrideValue] = useState<string>('');
	const [overrideType, setOverrideType] = useState<'string' | 'boolean'>('boolean');

	const onLogoutClick = () => {
		if (isNativeWebview()) {
			dismissWebview(DismissPayloadType.LOGOUT);
		} else {
			dispatch(logOutDev());
		}
	};

	const openOverrideModal = (key: string, experimentKey: string) => {
		setSelectedExperiment({ key, experimentKey });
		const currentOverride = overrides[experimentKey];
		if (currentOverride !== undefined) {
			if (typeof currentOverride === 'boolean') {
				setOverrideValue(currentOverride ? 'true' : 'false');
				setOverrideType('boolean');
			} else {
				setOverrideValue(currentOverride);
				setOverrideType('string');
			}
		} else {
			setOverrideValue('');
			setOverrideType('boolean');
		}
		setShowOverrideModal(true);
	};

	const saveOverride = useCallback(() => {
		if (!selectedExperiment) return;

		const valueToStore: OverrideValue | undefined =
			overrideValue.trim() === ''
				? undefined
				: overrideType === 'boolean'
					? overrideValue === 'true'
					: overrideValue;

		const { experimentKey } = selectedExperiment;
		if (valueToStore === undefined) {
			localStorage.removeItem(experimentKey);
			setOverrides((prev) => {
				const next = { ...prev };
				delete next[experimentKey];
				return next;
			});
		} else {
			localStorage.setItem(experimentKey, String(valueToStore));
			setOverrides((prev) => ({ ...prev, [experimentKey]: valueToStore }));
		}

		setShowOverrideModal(false);
		setSelectedExperiment(null);
		setOverrideValue('');
	}, [selectedExperiment, overrideValue, overrideType]);

	const clearOverride = useCallback(() => {
		if (!selectedExperiment) return;
		const { experimentKey } = selectedExperiment;
		localStorage.removeItem(experimentKey);
		setOverrides((prev) => {
			const next = { ...prev };
			delete next[experimentKey];
			return next;
		});
		setShowOverrideModal(false);
		setSelectedExperiment(null);
		setOverrideValue('');
	}, [selectedExperiment]);

	// Environment-based visibility check
	const shouldShowDevTools = () => {
		const env = window.Stash?.env;
		const allowedEnvs = ['__STASH_ENV__', 'review', 'local', 'edge', 'staging'];
		return allowedEnvs.includes(env);
	};

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.ctrlKey && event.key === 'd') {
				event.preventDefault();
				setIsHidden((prev) => !prev);
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [setIsHidden]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Element;
			const menu = document.querySelector('[data-dev-tools-menu]');
			const tab = document.querySelector('[data-dev-tools-tab]');

			if (isOpen && menu && tab && !menu.contains(target) && !tab.contains(target)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	// Don't render in production or if hidden
	if (!shouldShowDevTools() || isHidden) {
		return null;
	}

	return (
		<>
			<style>
				{`
					@keyframes bounce {
						0%, 100% {
							transform: translateY(0);
						}
						50% {
							transform: translateY(-4px);
						}
					}
				`}
			</style>
			{/* Full sidebar menu with tab anchored to left edge */}
			<div
				data-dev-tools-menu
				className={`fixed top-0 right-0 bottom-0 w-96 z-40 bg-background border-l border-outline overflow-visible ${
					isOpen ? 'shadow-elevationBottom1' : ''
				}`}
				style={{
					transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
					transition: 'transform 150ms ease-out',
					maxWidth: '75vw',
				}}
			>
				{/* Blue tab anchored to left edge of menu */}
				<button
					data-dev-tools-tab
					onClick={() => setIsOpen(!isOpen)}
					className="absolute top-2/3 left-0 transform -translate-y-1/2 -translate-x-full z-50 bg-primary text-onPrimary px-contentXS py-contentLarge shadow-elevationBottom1 flex items-center justify-center rounded-l-lg"
					style={{ width: '40px', height: '120px' }}
				>
					<span className="text-bodySmallStandard transform -rotate-90 whitespace-nowrap flex items-center gap-content2XS">
						<CaretUpIconWithStyle
							className={`w-content2XS h-content2XS transition-transform duration-150 ${
								isOpen ? 'rotate-180' : ''
							}`}
							style={
								!isOpen
									? {
											animation: 'bounce 1s infinite',
											animationTimingFunction: 'ease-in-out',
											transform: 'translateY(2px)',
										}
									: {}
							}
						/>
						dev tools
					</span>
				</button>
				<div className="px-contentMedium py-contentLarge overflow-y-auto h-full">
					{showOverrideModal && selectedExperiment ? (
						/* Override Editor */
						<div className="space-y-contentMedium">
							{/* Back button */}
							<button
								onClick={() => {
									setShowOverrideModal(false);
									setSelectedExperiment(null);
									setOverrideValue('');
								}}
								className="text-bodySmallStandard text-onSurface hover:text-primary transition-colors mb-contentMedium"
							>
								← Back
							</button>

							{/* Title */}
							<h3 className="text-headerSmall text-onSurface mb-contentMedium font-bold">
								{overrides[selectedExperiment.experimentKey] !== undefined
									? '⚠️ Overridden: '
									: 'Override: '}
								{selectedExperiment.key
									.replace(/_/g, ' ')
									.toLowerCase()
									.replace(/\b\w/g, (l) => l.toUpperCase())}
							</h3>

							{/* Editor content */}
							<div className="space-y-contentLarge">
								{/* Type Selection */}
								<fieldset>
									<legend className="text-bodyMediumEmphasize text-onSurface mb-contentSmall">
										Type
									</legend>
									<div className="flex gap-content2XS">
										<button
											onClick={() => setOverrideType('boolean')}
											className={`px-contentMedium py-contentSmall rounded-lg text-bodySmallStandard font-medium transition-all ${
												overrideType === 'boolean'
													? 'bg-primary text-onPrimary shadow-sm'
													: 'bg-containerLow text-onSurface hover:bg-container border border-outline'
											}`}
										>
											Boolean
										</button>
										<button
											onClick={() => setOverrideType('string')}
											className={`px-contentMedium py-contentSmall rounded-lg text-bodySmallStandard font-medium transition-all ${
												overrideType === 'string'
													? 'bg-primary text-onPrimary shadow-sm'
													: 'bg-containerLow text-onSurface hover:bg-container border border-outline'
											}`}
										>
											String
										</button>
									</div>
								</fieldset>

								{/* Value Input */}
								<fieldset>
									<legend className="text-bodyMediumEmphasize text-onSurface mb-contentSmall">
										Value
									</legend>
									{overrideType === 'boolean' ? (
										<div className="flex gap-content2XS">
											<button
												onClick={() => setOverrideValue('true')}
												className={`px-contentMedium py-contentSmall rounded-lg text-bodySmallStandard font-medium transition-all ${
													overrideValue === 'true'
														? 'bg-primary text-onPrimary shadow-sm'
														: 'bg-containerLow text-onSurface hover:bg-container border border-outline'
												}`}
											>
												True
											</button>
											<button
												onClick={() => setOverrideValue('false')}
												className={`px-contentMedium py-contentSmall rounded-lg text-bodySmallStandard font-medium transition-all ${
													overrideValue === 'false'
														? 'bg-primary text-onPrimary shadow-sm'
														: 'bg-containerLow text-onSurface hover:bg-container border border-outline'
												}`}
											>
												False
											</button>
										</div>
									) : (
										<input
											id="override-value"
											type="text"
											value={overrideValue}
											onChange={(e) => setOverrideValue(e.target.value)}
											placeholder="Enter string value"
											className="w-full px-contentMedium py-contentSmall border border-outline rounded-lg bg-background text-onSurface text-bodySmallStandard focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
										/>
									)}
								</fieldset>
							</div>

							{/* Actions */}
							<div className="space-y-contentSmall">
								<button
									onClick={saveOverride}
									className="w-full px-contentMedium py-contentSmall bg-primary text-onPrimary text-bodySmallStandard rounded-lg hover:bg-primaryContainer transition-all font-medium shadow-sm"
								>
									Save
								</button>
								{overrides[selectedExperiment.experimentKey] !== undefined && (
									<button
										onClick={clearOverride}
										className="w-full px-contentMedium py-contentSmall text-bodySmallStandard text-error hover:text-primary transition-colors font-medium border border-outline rounded-lg hover:bg-containerLow"
									>
										Clear
									</button>
								)}
							</div>
						</div>
					) : (
						/* Main Menu */
						<>
							{/* App Version */}
							<div className="mb-contentMedium">
								<div className="text-bodySmallStandard text-onSurfaceVariant">
									<span className="font-semibold">Version:</span> {window.Stash?.version || 'dev'}
								</div>
								<div className="text-bodySmallStandard text-onSurfaceVariant">
									<span className="font-semibold">Logged in:</span> {isLoggedIn ? 'Yes' : 'No'}
								</div>
							</div>

							<div className="space-y-contentMedium">
								{/* Actions section */}
								<div className="bg-container p-contentMedium rounded-lg">
									<button
										onClick={() => setIsHidden(true)}
										className="block w-full text-left text-bodySmallStandard hover:text-primary transition-colors mb-content2XS"
									>
										Hide Dev Tools (ctrl + d)
									</button>
									{isLoggedIn && (
										<button
											onClick={onLogoutClick}
											className="block w-full text-left text-bodySmallStandard text-error hover:text-primary transition-colors"
										>
											Log out
										</button>
									)}
								</div>

								{/* Experiment Overrides section */}
								<div className="bg-container p-contentMedium rounded-lg">
									<h3 className="text-bodyMediumEmphasize text-onSurface mb-contentSmall font-bold">
										Experiment Overrides
									</h3>
									<div className="space-y-content2XS">
										{Object.entries(EXPERIMENTS).map(([key, experimentKey]) => {
											const override = overrides[experimentKey];
											const hasOverride = override !== undefined;
											const displayValue = hasOverride ? override : false;

											return (
												<div key={key} className="flex items-start justify-between gap-content2XS">
													<button
														onClick={() => openOverrideModal(key, experimentKey)}
														className="text-bodySmallStandard text-onSurface flex-1 leading-tight text-left hover:text-primary transition-colors"
													>
														{hasOverride && '⚠️ '}
														{key
															.replace(/_/g, ' ')
															.toLowerCase()
															.replace(/\b\w/g, (l) => l.toUpperCase())}
													</button>
													<button
														onClick={() => openOverrideModal(key, experimentKey)}
														className={`w-6 h-3 rounded-full transition-colors flex-shrink-0 ${
															displayValue ? 'bg-primary' : 'bg-outline'
														}`}
													>
														<div
															className={`w-2 h-2 bg-background rounded-full transition-transform ${
																displayValue ? 'translate-x-3' : 'translate-x-0.5'
															}`}
														/>
													</button>
												</div>
											);
										})}
									</div>
								</div>
							</div>
						</>
					)}
				</div>
			</div>
		</>
	);
}

export default DevTools;
